import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Activity,
  Database 
} from 'lucide-react';

export interface KPIData {
  totalProcessed: number;
  approved: number;
  sentForCorrection: number;
  approvalRate: number;
  billableUnits: number;
  revenue: number;
  extractionMetrics:{
    batchesRun: number;
    avgChartsPerRun: number;
    chartsExtracted: number;
    
  }
  processingMetrics:{
    completionRate: number;
    pending: number;
    processed: number;
  }
  approvalMetrics:{
    auditorAgreed: number;
    agreementRate: number;
    aiApproved: number;
    yetToReview: number;
    auditorDisagreed: number;
      auditorReviewed: number;
  }
  correctionMetrics:{
    agreementRate: number;
    auditorAgreed: number;
    aiSendForCorrection: number;
    yetToReview: number;
    auditorDisagreed: number;
      auditorReviewed: number;
  }
  todayMetrics:{
    chartsExtracted: number;
    audited:{
      aiProcessed: number;
      auditorReviewed: number;
    }
    approved:{
      auditorApproved: number;
      aiApproved: number;
    }
    correction:{
      aiSentForCorrection: number;
      auditorApproved: number;
    }
  }
  
}

interface KPICardsProps {
  data: KPIData;

}

interface HoverCardProps {
  children: React.ReactNode;
  content: React.ReactNode;
}

export function DashboardKPICards({ data}: KPICardsProps) {
  const calculateTrend = (current: number, previous?: number) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      percentage: Math.abs(change),
      isPositive: change >= 0,
      value: change
    };
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // const totalTrend = calculateTrend(data?.totalProcessed, previousData?.totalProcessed);
  // const approvedTrend = calculateTrend(data?.approved, previousData?.approved);
  // const correctionTrend = calculateTrend(data?.sentForCorrection, previousData?.sentForCorrection);
  // const rateTrend = calculateTrend(data?.approvalRate, previousData?.approvalRate);
  // Tooltips
//   const HoverCard: React.FC<HoverCardProps> = ({ children, content }) => {
//   const [open, setOpen] = useState(false);
//   const [align, setAlign] = useState<"center" | "top" | "bottom">("center");
//   const ref = useRef<HTMLDivElement>(null);

//   useEffect(() => {
//     if (open && ref.current) {
//       const rect = ref.current.getBoundingClientRect();
//       const spaceBelow = window.innerHeight - rect.bottom;
//       const spaceAbove = rect.top;

//       // if not enough space below, align from bottom
//       if (spaceBelow < 150) {
//         setAlign("bottom");
//       } 
//       // if not enough space above, align from top
//       else if (spaceAbove < 150) {
//         setAlign("top");
//       } 
//       else {
//         setAlign("center");
//       }
//     }
//   }, [open]);

//   return (
//     <div
//       ref={ref}
//       className="relative inline-block"
//       onMouseEnter={() => setOpen(true)}
//       onMouseLeave={() => setOpen(false)}
//     >
//       {children}
//       {open && (
//         <div
//           className={`absolute top-full mb-2 left-0  z-50 w-64 rounded-2xl bg-white p-3 shadow-xl border
//             ${
//               align === "center"
//                 ? "top-1/2 -translate-y-1/2"
//                 : align === "top"
//                 ? "top-0"
//                 : "bottom-0"
//             }`}
//         >
//           {content}
//         </div>
//       )}
//     </div>
//   );
// };
const HoverCard: React.FC<HoverCardProps> = ({ children, content }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={ref}
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}
      {open && (
        <div
          className="absolute left-0 top-full mt-2 z-50 w-52 rounded-2xl bg-white p-3 shadow-xl border text-left"
        >
          {content}
        </div>
      )}
    </div>
  );
};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Extraction Metrics */}
      <Card className="bg-white hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-3 px-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Extraction Metrics
          </CardTitle>
          <Database className="h-3 w-3 text-purple-600" />
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
            {/* Batches Run */}
            <div className="text-center">
              <div className="text-base font-bold text-purple-900 mb-0.5">
                {/* {formatNumber(Math.ceil(data?.extractionMetrics?.batchesRun/ 425))}
                 */}
                 {data.extractionMetrics?.batchesRun||0}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                Batches Run
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="flex justify-center">
              <div className="w-px bg-border h-full"></div>
            </div>

            {/* Charts Extracted */}
            <div className="text-center">
              <div className="text-base font-bold text-violet-900 mb-0.5">
                {/* {formatNumber(Math.floor(data?.processed * 1.15))}
                 */}
                 {data?.extractionMetrics?.chartsExtracted||0}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                Charts Extracted
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-border my-2"></div>

          {/* Average charts / run */}
          <div className="text-center">
            <div className="text-lg font-bold text-indigo-900 mb-0.5">
              {/* {Math.floor(data?.processed * 1.15 / Math.ceil(data?.processed / 425))}
               */}
               {data?.extractionMetrics?.avgChartsPerRun||0}
            </div>
            <div className="text-xs text-muted-foreground leading-tight">
              Avg Charts / Run
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Processing Metrics */}
      <Card className="bg-white hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-3 px-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Processing Metrics
          </CardTitle>
          <FileText className="h-3 w-3 text-blue-600" />
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
            {/* Processed */}
            <div className="text-center">
              <div className="text-base font-bold text-blue-900 mb-0.5">
                {formatNumber(data?.processingMetrics?.processed||0)}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                Processed
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="flex justify-center">
              <div className="w-px bg-border h-full"></div>
            </div>

            {/* Pending */}
            <div className="text-center">
              <div className="text-base font-bold text-slate-900 mb-0.5">
                {/* {formatNumber(Math.floor(data?.totalProcessed * 0.12))}
                 */}
                   {formatNumber(data?.processingMetrics?.pending||0)}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                Pending
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-border my-2"></div>

          {/* % Processed */}
          <div className="text-center">
            <div className="text-lg font-bold text-indigo-900 mb-0.5">
              {/* {((data?.processed / (data?.processed + Math.floor(data?.processed * 0.12))) * 100).toFixed(1)}% */}
              {data?.processingMetrics?.completionRate||0}
            </div>
            <div className="text-xs text-muted-foreground leading-tight">
              Completion Rate
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Approved */}
      <Card className="bg-white hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-3 px-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Approval Metrics
          </CardTitle>
          <CheckCircle className="h-3 w-3 text-green-600" />
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
            {/* AI Approved */}
            <div className="text-center">
              <div className="text-base font-bold text-green-900 mb-0.5">
                {formatNumber(data?.approvalMetrics?.aiApproved||0)}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                AI Approved
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="flex justify-center">
              <div className="w-px bg-border h-full"></div>
            </div>

            {/* Auditor Agreed */}
            <div className="text-center">
              <div className="text-base font-bold text-blue-900 mb-0.5">
                {/* {formatNumber(Math.floor(data?.approved * 0.85))}
                 */}
                 {formatNumber(data?.approvalMetrics?.auditorAgreed||0)}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                Auditor Agreed
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-border my-2"></div>

          {/* % Agreed */}
          <div className="text-center">
            <div className="text-lg  text-emerald-900 mb-0.5">
              {/* {data?.totalProcessed > 0 
                ? `${(Math.floor(data?.approved * 0.85) / data?.approved * 100).toFixed(1)}%`
                : '0%'
              } */}
               <HoverCard
    content={
      <div className="text-sm space-y-1 text-black">
        <p className="font-semibold">Approval Metrics Details</p>
        <p>Auditor Reviewed: {data?.approvalMetrics?.auditorReviewed ?? 0}</p>
        <p>Auditor Disagreed: {data?.approvalMetrics?.auditorDisagreed ?? 0}</p>
        <p>Yet to Review: {data?.approvalMetrics?.yetToReview ?? 0}</p>
      </div>
    }
  >
<div className='text-lg  text-emerald-900 font-bold'> {data?.approvalMetrics?.agreementRate||0}</div>
  </HoverCard>
             
            </div>
            <div className="text-xs text-muted-foreground leading-tight">
              Agreement Rate
              
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sent for Correction */}
      <Card className="bg-white hover:shadow-md transition-shadow">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-3 px-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Correction Metrics
          </CardTitle>
          <AlertTriangle className="h-3 w-3 text-orange-600" />
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
            {/* AI Send for Correction */}
            <div className="text-center">
              <div className="text-base font-bold text-orange-900 mb-0.5">
                {formatNumber(data?.correctionMetrics?.aiSendForCorrection||0)}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                AI Send for Correction
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="flex justify-center">
              <div className="w-px bg-border h-full"></div>
            </div>

            {/* Auditor Agreed */}
            <div className="text-center">
              <div className="text-base font-bold text-amber-900 mb-0.5">
                {/* {formatNumber(Math.floor(data?.sentForCorrection * 0.72))}
                 */}
                 {formatNumber(data?.correctionMetrics?.auditorAgreed||0)}
              </div>
              <div className="text-xs text-muted-foreground leading-tight">
                Auditor Agreed
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-border my-2"></div>

          {/* % Agreed */}
          <div className="text-center">
            <div className="text-lg mb-0.5">
              {/* {data?.sentForCorrection > 0 
                ? `${(Math.floor(data?.sentForCorrection * 0.72) / data?.sentForCorrection * 100).toFixed(1)}%`
                : '0%'
              } */}
              <HoverCard
    content={
      <div className="text-sm space-y-1 text-black">
        <p className="font-semibold">Correction Metrics Details</p>
        <p>Auditor Reviewed: {data?.correctionMetrics?.auditorReviewed ?? 0}</p>
        <p>Auditor Disagreed: {data?.correctionMetrics?.auditorDisagreed ?? 0}</p>
        <p>Yet to Review: {data?.correctionMetrics?.yetToReview ?? 0}</p>
      </div>
    }
  >
<div className='text-lg  font-bold text-red-900'> {data?.correctionMetrics?.agreementRate||0}</div>
  </HoverCard>
              
            </div>
            <div className="text-xs text-muted-foreground leading-tight">
              Agreement Rate
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Approval Rate */}

    </div>
  );
}